'use client';

import { useState } from 'react';
import AppFooter from '@/components/AppFooter';
import AppLogo from '@/components/AppLogo';
import BottomNavigation from '@/components/BottomNavigation';
import ExportImportPanel from '@/components/ExportImportPanel';
import GoogleDriveBackupPanel from '@/components/GoogleDriveBackupPanel';
import { useAppSettings } from '@/hooks/useAppSettings';
import { useCategories } from '@/hooks/useCategories';
import { useRecurringExpenses } from '@/hooks/useRecurringExpenses';
import { useSavingsGoal } from '@/hooks/useSavingsGoal';
import { useTags } from '@/hooks/useTags';
import { useTransactions } from '@/hooks/useTransactions';
import { SUPPORTED_CURRENCIES } from '@/lib/money';

export default function SettingsPage() {
  const { transactions, resetData, error: transactionsError } = useTransactions();
  const { categories, resetData: resetCategories, error: categoriesError } = useCategories();
  const { tags, resetData: resetTags, error: tagsError } = useTags();
  const { recurringExpenses, resetData: resetRecurringExpenses, error: recurringError } = useRecurringExpenses();
  const { resetData: resetSavingsGoals, error: savingsError } = useSavingsGoal();
  const { settings, updateSettings, resetSettings, error: settingsError } = useAppSettings();
  const [confirm, setConfirm] = useState(false);
  const pageError = transactionsError || categoriesError || tagsError || recurringError || savingsError || settingsError;
  const selectedCurrency = SUPPORTED_CURRENCIES.find((currency) => currency.code === settings.currency);

  const resetAll = async () => {
    await resetData();
    await resetCategories();
    await resetTags();
    await resetRecurringExpenses();
    await resetSavingsGoals();
    await resetSettings();
    setConfirm(false);
    window.location.reload();
  };

  return (
    <main className="page-shell">
      <div className="page-inner">
        {pageError ? (
          <div className="rounded-2xl border border-rose-900/70 bg-rose-950 p-4 text-sm text-rose-200">
            {pageError}
          </div>
        ) : null}

        <section className="overflow-hidden rounded-2xl border border-brand-500/20 bg-slate-900 shadow-sm">
          <div className="bg-brand-700 px-4 py-5 text-white min-[380px]:px-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0">
                <AppLogo className="text-brand-100" markClassName="border-white/25" textClassName="text-brand-100" />
                <h1 className="mt-1 text-xl font-semibold leading-tight text-white min-[380px]:text-2xl">
                  Données et préférences
                </h1>
                <p className="mt-2 text-sm leading-6 text-brand-100">
                  Sauvegardez, restaurez et gardez le contrôle de vos données locales.
                </p>
              </div>
              <div className="rounded-2xl bg-slate-950/45 p-3 sm:w-44">
                <p className="text-xs font-medium text-brand-100">Devise active</p>
                <p className="mt-1 text-lg font-semibold text-brand-100">{settings.currency}</p>
              </div>
            </div>
          </div>

          <div className="grid gap-px bg-slate-800 sm:grid-cols-4">
            <div className="bg-slate-900 p-4">
              <p className="text-sm font-medium text-slate-400">Transactions</p>
              <p className="mt-2 text-2xl font-semibold text-slate-100">{transactions.length}</p>
            </div>
            <div className="bg-slate-900 p-4">
              <p className="text-sm font-medium text-slate-400">Catégories</p>
              <p className="mt-2 text-2xl font-semibold text-brand-200">{categories.length}</p>
            </div>
            <div className="bg-slate-900 p-4">
              <p className="text-sm font-medium text-slate-400">Tags</p>
              <p className="mt-2 text-2xl font-semibold text-slate-100">{tags.length}</p>
            </div>
            <div className="bg-slate-900 p-4">
              <p className="text-sm font-medium text-slate-400">Charges</p>
              <p className="mt-2 text-2xl font-semibold text-slate-100">{recurringExpenses.length}</p>
            </div>
          </div>
        </section>

        <section className="section-card">
          <div className="mb-5">
            <h2 className="text-lg font-semibold text-slate-100">Sauvegarde locale</h2>
            <p className="mt-2 text-sm text-slate-400">
              Exportez un fichier complet, ou restaurez une sauvegarde Licaio quand vous changez d'appareil.
            </p>
          </div>
          <ExportImportPanel />
        </section>

        <section className="section-card">
          <div className="mb-5">
            <h2 className="text-lg font-semibold text-slate-100">Sauvegarde Google Drive</h2>
            <p className="mt-2 text-sm leading-6 text-slate-400">
              Gardez une copie récupérable sur votre compte Google, sans créer de compte Licaio.
            </p>
          </div>
          <GoogleDriveBackupPanel />
        </section>

        <section className="section-card">
          <div className="mb-5 flex flex-col gap-3 min-[420px]:flex-row min-[420px]:items-start min-[420px]:justify-between">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-slate-100">Devise principale</h2>
              <p className="mt-2 text-sm leading-6 text-slate-400">
                Elle sert uniquement à afficher les montants. Les transactions existantes ne sont pas converties.
              </p>
            </div>
            <span className="w-fit rounded-full bg-slate-800 px-3 py-1 text-xs font-semibold text-slate-300">
              {selectedCurrency?.label ?? settings.currency}
            </span>
          </div>

          <label className="block">
            <span className="text-sm font-medium text-slate-200">Devise</span>
            <select
              value={settings.currency}
              onChange={(event) => updateSettings({ currency: event.target.value })}
              className="mt-2 w-full px-4 py-3"
            >
              {SUPPORTED_CURRENCIES.map((currency) => (
                <option key={currency.code} value={currency.code}>
                  {currency.code} - {currency.label}
                </option>
              ))}
            </select>
          </label>
        </section>

        <section className="grid gap-5 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="section-card">
            <h2 className="text-lg font-semibold text-slate-100">Confidentialité</h2>
            <div className="mt-4 space-y-3 text-sm leading-6 text-slate-300">
              <p>Licaio fonctionne sans compte et stocke vos informations uniquement dans le navigateur de cet appareil.</p>
              <p>Aucune donnée n'est envoyée sur un serveur. Vos sauvegardes restent sous votre contrôle.</p>
              <p>Restaurez seulement des fichiers que vous reconnaissez.</p>
            </div>
          </div>

          <div className="section-card border-rose-900/70 bg-rose-950/40">
            <h2 className="text-lg font-semibold text-rose-100">Réinitialiser</h2>
            <p className="mt-2 text-sm leading-6 text-rose-200/80">
              Cette action vide toutes les données locales et remet l'application à zéro.
            </p>
            <div className="mt-5 flex flex-col gap-3">
              <button type="button" className="danger-button w-full" onClick={() => setConfirm(true)}>
                Réinitialiser les données
              </button>
              {confirm ? (
                <div className="rounded-2xl border border-rose-900/70 bg-slate-950 p-3">
                  <p className="text-sm text-rose-200">Confirmez pour supprimer toutes les données locales.</p>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <button type="button" className="secondary-button px-3 py-2" onClick={() => setConfirm(false)}>
                      Annuler
                    </button>
                    <button type="button" className="danger-button px-3 py-2" onClick={resetAll}>
                      Confirmer
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-800 bg-slate-950 p-4">
          <div className="flex flex-col gap-1 min-[420px]:flex-row min-[420px]:items-center min-[420px]:justify-between">
            <p className="text-sm font-medium text-slate-400">Licaio</p>
            <p className="text-sm font-semibold text-slate-300">Version 1.3.1</p>
          </div>
        </section>
      </div>
      <BottomNavigation active="/settings" />
      <div className="page-inner mt-5">
        <AppFooter />
      </div>
    </main>
  );
}
